import { observable, action, reaction } from 'mobx';

import { PROCESSOR_IDS } from '../constants/processors';
import {
  addDataForGiftAndItemProduct,
  decideOriginalDomain,
  decidePromoInfo,
  fetchUserName,
  generateSiteSubcat,
  getPaymentProcessor,
  redirectToProcessor,
} from '../components/paymentComponents/PaymentUtils';
import { getCookie } from '../utils/helpers';
import { EXPIRED_PROMOS, PROMO_CODES } from '../constants/promoCodes';
import { getPurchaseInfo } from '../components/accountComponents/services';
import { PRODUCT_TYPES } from '../constants/ProductTypes';
import { validateEmailOnNakedNews } from '../utils/emailValidation';

import PaymentFallbackStore from './PaymentFallbackStore';
import NotificationStore from './NotificationStore';
import CampaignStore from './CampaignStore';
import RootStore from './RootStore';
import { newPasswordValid } from '../utils/passwordValidationUtils';

const RETURN_PATH_FOR_RECURRING_TX = '/checkout';

class CheckoutStore {
  @observable programs = [];
  @observable username = [];
  @observable activeProgram = {};
  @observable processingPayment = false;
  @observable selectedPaymentMethod = 'CC';
  @observable epochAvailable = false;
  @observable createdUser = '';
  @observable user = {};
  @observable additionalInfo = null;

  @observable showOverlay = false;
  @observable oneClickPaymentData = null;
  @observable promoCodeInfo = {
    pcode: null,
    affiliateCode: null,
  };

  @observable promoInUrl = {
    hash: null,
    affiliateCode: null,
  };

  @observable paymentLoading = false;
  @observable availableProcessors = [];
  @observable oldPrices = this.programs;
  @observable shouldAnimatePromo = false;
  @observable productData = {};
  @observable userEmailFromUrl = null;
  @observable submitButtonDisabled = false;
  @observable productType = null;
  @observable currentlySelectedAnchor = {};
  @observable email = null;
  @observable password = null;
  @observable addCrossSell = true;

  constructor() {
    this.setupReactions();
  }

  setupReactions() {
    // Reaction to update properties when activeProgram changes
    reaction(
      () => this.programs,
      () => {
        this.activeProgram = this.programs?.[0];
        this.epochAvailable = this.activeProgram?.categories.find(
          (category) => category.type === 'EW'
        );
        this.paysafeAvailable = this.activeProgram?.categories.find(
          (category) => category.type === 'CC'
        );
        this.oneClickPaymentData = this.getOneClickPaymentData();
        this.availableProcessors = this.activeProgram?.categories[0]?.methods;
      }
    );
  }

  @action setEmail = (e) => {
    this.email = e.target.value;
  };

  @action setPassword = (e) => {
    this.password = e.target.value;
  };

  getOneClickPaymentData = () =>
    this.activeProgram?.categories?.find(
      (category) => category?.methods[0]?.hasOneClick
    );

  @action setProductType = (value) => {
    this.productType = value;
  };

  @action setPrograms = (programs) => {
    this.programs = programs;
  };

  @action setActiveProgram = (selectedProgram) => {
    this.activeProgram = selectedProgram;
  };

  @action setPromoCodeInfo = (affiliateCode, pcode) => {
    this.promoCodeInfo = {
      pcode,
      affiliateCode,
    };
  };

  @action setPromoInUrl = (hash, affiliateCode, pcode) => {
    this.promoInUrl = {
      hash,
      affiliateCode,
      pcode,
    };
  };

  @action setAnimationState = (value) => {
    this.shouldAnimatePromo = value;
  };

  @action onPaymentMethodChange = (e) => {
    this.selectedPaymentMethod = e.target.value;
  };

  @action setSelectedPaymentMethod = (value) => {
    this.selectedPaymentMethod = value;
  };

  @action setProductData = (value) => {
    this.productData = value;
  };

  @action setUserData = (userData, userEmailFromUrl) => {
    this.user = userData;
    this.userEmailFromUrl = userEmailFromUrl;
  };

  @action setShowOverlay = (value) => {
    this.showOverlay = value;
  };

  generateProcessorData = (data) => {
    const { currentCampaign } = CampaignStore;
    const hasAtc = data?.affiliateCode;
    const promoBlockedByAtc = hasAtc === PROMO_CODES.CAM4_SOURCE;
    const fanDiscountActive =
      hasAtc === PROMO_CODES.CAM4_ANCHOR_SOURCE ||
      hasAtc === PROMO_CODES.PROMO_ANCHOR_SOURCE;
    const processorData = decidePromoInfo(
      false,
      currentCampaign,
      promoBlockedByAtc,
      this.promoCodeInfo,
      {},
      false,
      false,
      fanDiscountActive,
      hasAtc,
      this.promoInUrl,
      data,
      this.userEmailFromUrl
    );

    return addDataForGiftAndItemProduct(
      processorData,
      this.productData?.shortUrlParam
    );
  };

  formatEpayReqBody = (dataObject, isLifetimeProduct) => {
    const {
      amount,
      orderId,
      productType,
      productDetails: {
        recurringAmount,
        recurringPeriod,
        recurringPeriodUnit,
        initialPeriod,
        initialPeriodUnit,
      },
      crossSellOff,
      crossSellOrderId,
      hash,
      userName,
      crossSell,
    } = dataObject || {};
    let resultPayload = {
      amount: String(amount),
      orderId: String(orderId),
      recurringAmount: String(recurringAmount),
      recurringPeriod,
      recurringPeriodUnit,
      initialPeriod,
      initialPeriodUnit,
      hash,
      userName,
      xsellOff: String(crossSellOff),
      siteSubcat: generateSiteSubcat(
        decideOriginalDomain(),
        this.productData.shortUrlParam || window.location.pathname
      ),
    };
    if (
      [PRODUCT_TYPES.CAMEO_NON_NUDE, PRODUCT_TYPES.CAMEO_NUDE].includes(
        productType
      ) ||
      isLifetimeProduct
    ) {
      return {
        initialPeriod: String(initialPeriod),
        initialPeriodUnit,
        userName,
        orderId: String(orderId),
        amount: String(amount),
        hash,
      };
    }

    if (productType === 'FANCLUB' || !crossSellOrderId || !crossSell) {
      return resultPayload;
    }

    const {
      id,
      xsellAmount,
      xsellRecurringAmount,
      xsellRecurringPeriod,
      xsellRecurringPeriodUnit,
      xsellInitialPeriod,
      xsellInitialPeriodUnit,
      xsellSiteSubcat,
    } = crossSell || {};

    resultPayload = {
      ...resultPayload,
      xsellAmount: String(xsellAmount),
      xsellRecurringAmount: String(xsellRecurringAmount),
      xsellRecurringPeriod: String(xsellRecurringPeriod),
      xsellRecurringPeriodUnit,
      xsellInitialPeriod: String(xsellInitialPeriod),
      xsellInitialPeriodUnit,
      xsellId: String(id),
      xsellOrderId: String(crossSellOrderId),
      xsellSiteSubcat,
    };

    return resultPayload;
  };

  shouldUseNonDynamicPayload = (pcode) =>
    [
      PROMO_CODES.PLAYBOY_PROMO_2024,
      PROMO_CODES.CAMS_BB,
      PROMO_CODES.NNLIVEMRSK6,
      PROMO_CODES.PORN_DUDE,
      PROMO_CODES.EVENTS_24,
      PROMO_CODES.ASNWEEK2024,
      PROMO_CODES.LIFETIMEPBNOV24,
      PROMO_CODES.BLACKFRIDAY24AFF,
      PROMO_CODES.CYBERMONDAY24AFF,
    ].includes(pcode);

  isLifetimeProduct = (trackingCode) =>
    [
      PROMO_CODES.CAMS_BB,
      PROMO_CODES.NNLIVEMRSK6,
      PROMO_CODES.PORN_DUDE,
      PROMO_CODES.NNLIVEMRSK5,
      PROMO_CODES.PLAYBOY_PROMO_2024,
      PROMO_CODES.LIFETIMEPBNOV24,
      PROMO_CODES.BLACKFRIDAY24AFF,
      PROMO_CODES.CYBERMONDAY24AFF,
      PROMO_CODES.NNLIVEMRSK7,
    ].includes(trackingCode);

  shouldGetDynamicPricingPayloadData = () =>
    [
      PRODUCT_TYPES.MEMBERSHIP,
      PRODUCT_TYPES.FANCLUB,
      PRODUCT_TYPES.CAMEO_NON_NUDE,
      PRODUCT_TYPES.CAMEO_NUDE,
    ].includes(this.productType);

  getPayload = async (data, userName) => {
    const processorData = { ...data };
    let payload = {};
    const tracingCode = data?.pcode || data?.passThrough?.campaignAffiliateCode;
    const isLifetimeProduct = this.isLifetimeProduct(tracingCode);

    if (
      this.shouldGetDynamicPricingPayloadData() &&
      !this.shouldUseNonDynamicPayload(data?.passThrough?.campaignAffiliateCode)
    ) {
      payload = await this.getProcessorData(data, userName, isLifetimeProduct);
      delete processorData.passThrough;
      delete processorData.username;
      delete processorData.token;
    }
    if (this.shouldUseNonDynamicPayload && !isLifetimeProduct) {
      processorData.pcode = data?.passThrough?.campaignAffiliateCode;
    }
    delete processorData.isAtc;
    const emailValue = this.email || RootStore?.loggedInUser?.account?.email;
    if (emailValue) {
      processorData.email = emailValue;
    }

    return { processorPayload: processorData, payload };
  };

  getDynamicDesc = (processorId) => {
    if (
      [PRODUCT_TYPES.CAMEO_NON_NUDE, PRODUCT_TYPES.CAMEO_NUDE].includes(
        this.productType
      ) &&
      processorId === PROCESSOR_IDS.SEGPAY
    ) {
      return { dynamicdesc: 'Personal Message Order' };
    }

    if (this.productType === PRODUCT_TYPES.FANCLUB) {
      return { dynamicdesc: 'Fanclub Subscription' };
    }

    if (this.productType === PRODUCT_TYPES.MEMBERSHIP) {
      return { dynamicdesc: 'Membership Subscription' };
    }

    return {};
  };

  processPurchaseRequest = async (
    processorId,
    priceId,
    username,
    processorData
  ) => {
    const data = this.generateProcessorData(processorData);
    const { payload: extraPayload, processorPayload } = await this.getPayload(
      data,
      username
    );
    const userName = extraPayload?.userName || username;
    const dynamicDesc = this.getDynamicDesc(processorId);
    getPaymentProcessor(
      processorId,
      priceId,
      userName,
      processorPayload,
      extraPayload
    )
      .then((processorRes) => {
        this.processingPayment = false;
        this.paymentLoading = false;
        redirectToProcessor(processorRes.data.nextStage.url, {
          ...processorRes?.data?.nextStage?.parameters,
          ...dynamicDesc,
        });
      })
      .catch(() => {
        PaymentFallbackStore.changeProcessorForUser(this.user, processorId);
        NotificationStore.error(
          'There was a problem with payment. Please try again.'
        );
        this.processingPayment = false;
        this.paymentLoading = false;
      });
  };

  @action getProcessorData = (processorData, userName, isLifetimeProduct) =>
    new Promise(async (resolve, reject) => {
      try {
        let extraPayload = {};
        const { data, error } = await getPurchaseInfo(
          this.activeProgram?.id,
          this.productData?.anchorToSubscribe,
          processorData,
          this.additionalInfo,
          userName,
          !this.addCrossSell,
        );
        if (!error) {
          extraPayload = this.formatEpayReqBody(data, isLifetimeProduct);
        }

        resolve(extraPayload);
      } catch (error) {
        reject(error);
      }
    });

  @action handleFanclubPurchaseForm = async (
    program,
    processorId,
    userName
  ) => {
    const originalDomain = decideOriginalDomain();
    const priceId = program?.priceId || program?.id;
    const affiliateCode = getCookie('affiliateCode') || getCookie('_atc');

    let processorData = {};
    processorData = {
      originalDomain,
      returnPath: RETURN_PATH_FOR_RECURRING_TX,
      paymentType: this.selectedPaymentMethod === 'CC' ? 'CC' : 'PP',
    };

    if (
      (affiliateCode && !EXPIRED_PROMOS.includes(affiliateCode)) ||
      this.user?.features?.includes(affiliateCode)
    ) {
      processorData.affiliateCode = affiliateCode;
    }

    if (this.productData?.anchorToSubscribe) {
      processorData.productType = 'FANCLUB';
      processorData.subCategory = String(this.productData?.anchorToSubscribe);
      processorData.siteSubcat = generateSiteSubcat(
        originalDomain,
        this.productData.shortUrlParam
      );
    }

    this.paymentLoading = true;

    let username =
      this.user?.user?.username ||
      sessionStorage.getItem('username') ||
      userName ||
      this.createdUser;

    if (!username) {
      const { data, error } = await fetchUserName();
      if (!error) {
        username = data?.username;
        this.createdUser = data?.username;
      }
    }

    const newToken = sessionStorage.getItem('token') || this.user?.user?.token;

    const { xSellPriceIds, ...renewalData } = processorData;
    const data = {
      token: newToken,
      ...renewalData,
    };

    if (this.user?.authenticated) {
      data.username = username;
    }

    this.processPurchaseRequest(processorId, priceId, username, data);
  };

  getProcessorId = async () => {
    if (!this.user?.authenticated) {
      return PaymentFallbackStore.processorToUse;
    }

    const data = await PaymentFallbackStore.getProcessorForUser(this.user);

    if (
      this.user?.authenticated &&
      data?.data?.useAlternativePaymentProcessor
    ) {
      return PROCESSOR_IDS.SEGPAY;
    }

    return PROCESSOR_IDS.EPOCH;
  };

  doCheckout = async (userName) => {
    const processorId = await this.getProcessorId();
    this.handleFanclubPurchaseForm(this.activeProgram, processorId, userName);
  };

  handlePasswordError = (error) => NotificationStore.error(error);

  userInputsAreValid = async () => {
    const isEmailValid = await validateEmailOnNakedNews(this.email);
    if (!isEmailValid) {
      NotificationStore.error('Please enter a valid email address.');
      this.paymentLoading = false;

      return false;
    }

    if (!newPasswordValid(this.password, this.password, this.handlePasswordError)) {
      this.paymentLoading = false;

      return false;
    }

    return true;
  }

  @action onSubmitClick = async (
    e,
    submitCallback,
    bypassAccountCreation = false
  ) => {
    if (e) {
      e.preventDefault();
    }
    this.paymentLoading = true;

    if (bypassAccountCreation) {
      this.doCheckout();

      return;
    }

    if (RootStore.loggedInUser || this.user?.authenticated) {
      this.doCheckout(RootStore.loggedInUser?.account?.username);

      return;
    }

    if (!await this.userInputsAreValid()) {
      this.paymentLoading = false;
      return;
    }

    try {
      const data = await fetchUserName(this.email, this.password);
      if (submitCallback) {
        submitCallback(e, data?.data?.username);
      }

      this.doCheckout(data?.data?.username);
    } catch (error) {
      this.paymentLoading = false;
      NotificationStore.error('Email already in use, please login.');
    }
  };

  @action handlePackageChange = (e) => {
    console.log(e.target.value)
    const targetProgram = this.programs?.find(
      (program) => program?.id === parseInt(e.target.value, 10)
    );
    const paysafeAvailable = targetProgram?.categories.find(
      (category) => category.type === 'CC'
    );
    const epochAvailable = targetProgram?.categories.find(
      (category) => category.type === 'EW'
    );

    this.activeProgram = targetProgram;
    this.epochAvailable = epochAvailable;
    this.paysafeAvailable = paysafeAvailable;

    if (!epochAvailable || !paysafeAvailable) {
      let selectedPaymentMethod = null;
      if (!epochAvailable) {
        selectedPaymentMethod = 'CC';
      } else if (!paysafeAvailable) {
        selectedPaymentMethod = 'EW';
      }

      this.selectedPaymentMethod = selectedPaymentMethod;
    }
  };

  formatProductDataObject = (products, epayData, isLifetime) => {
    const epayProductData = isLifetime
      ? epayData?.find((data) => data?.details?.lifetime === true)
      : epayData?.[0];
    const formattedProducts = [...products];

    formattedProducts.forEach((product) => {
      product.priceId = epayProductData?.id;
      product.currency = epayProductData?.currency;
      product.categories = epayProductData?.categories;
      product.details.trialPeriod = product?.details?.initialPeriod;
      product.details.trialUnit = product?.details?.initialPeriodUnit;
      product.details.recurringPeriodType =
        product?.details?.recurringPeriodUnit;
    });

    return formattedProducts;
  };

  @action setSubmitButtonDisabled = (value) => {
    this.submitButtonDisabled = value;
  };

  @action setCurrentlySelectedAnchor = (value) => {
    this.currentlySelectedAnchor = value;
  };

  @action setAdditionalInfo = (value) => {
    this.additionalInfo = value;
  };

  @action setOldPrices = (prices) => {
    this.oldPrices = prices;
  }

  @action setCrossSellOff = (value) => {
    this.addCrossSell = value;
  }

  @action resetState = () => {
    this.programs = [];
    this.username = [];
    this.activeProgram = {};
    this.processingPayment = false;
    this.selectedPaymentMethod = 'CC';
    this.epochAvailable = false;
    this.createdUser = '';
    this.user = {};
    this.showOverlay = false;
    this.oneClickPaymentData = null;
    this.promoCodeInfo = {
      pcode: null,
      affiliateCode: null,
    };
    this.promoInUrl = {
      hash: null,
      affiliateCode: null,
      pcode: null,
    };
    this.paymentLoading = false;
    this.availableProcessors = [];
    this.oldPrices = this.programs;
    this.shouldAnimatePromo = false;
    this.productData = {};
    this.userEmailFromUrl = null;
    this.submitButtonDisabled = false;
    this.productType = null;
    this.additionalInfo = null;
    this.email = null;
    this.password = null;
    this.addCrossSell = false;
  };
}

export default new CheckoutStore();
